import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { CircleNotch, UploadSimple, Users } from "@phosphor-icons/react";

/* Reusable audience selector: CSV upload OR a Salesforce source (Contacts / Leads /
   List View / Report / Campaign) with a filter builder + live preview. Mirrors the
   WhatsApp Broadcasts picker so Journeys enrolment has full audience parity.
   Calls onChange({ dataSource, records, ready }) whenever the selection changes. */

function parseCsv(text) {
    const lines = (text || "").split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (names) => headers.findIndex((h) => names.includes(h));
    const pi = idx(["phone", "mobile", "phone number", "mobilephone", "number"]);
    const ni = idx(["name", "full name", "contact name"]);
    const ci = idx(["company", "account", "organization"]);
    const ei = idx(["email", "email address"]);
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(",").map((c) => c.trim());
        const phone = (pi >= 0 ? cells[pi] : cells[0]) || "";
        if (!phone) continue;
        rows.push({
            name: ni >= 0 ? cells[ni] || "" : "", phone,
            company: ci >= 0 ? cells[ci] || "" : "", email: ei >= 0 ? cells[ei] || "" : "",
        });
    }
    return rows;
}

function FilterSelect({ label, opts, val, onChange, tid, prefix }) {
    const items = (opts || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    return (
        <label className="text-xs text-stone2">{label}
            <select data-testid={`${prefix}-filter-${tid}`} value={val || ""} onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
                <option value="">Any</option>
                {items.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}

const SOURCES = [["csv", "CSV upload"], ["sf_contacts", "SF Contacts"], ["sf_leads", "SF Leads"],
    ["sf_listview", "SF List View"], ["sf_report", "SF Report"], ["sf_campaign", "SF Campaign"]];

export function AudiencePicker({ tidPrefix = "audience", onChange }) {
    const [sourceKind, setSourceKind] = useState("csv");
    const [csvRows, setCsvRows] = useState([]);
    const [csvName, setCsvName] = useState("");
    const [manual, setManual] = useState("");
    const [filters, setFilters] = useState({});
    const [filterOpts, setFilterOpts] = useState({});
    const [lvObjects, setLvObjects] = useState([]);
    const [lvObject, setLvObject] = useState("");
    const [listviews, setListviews] = useState([]);
    const [listViewId, setListViewId] = useState("");
    const [reports, setReports] = useState([]);
    const [reportId, setReportId] = useState("");
    const [phoneColumn, setPhoneColumn] = useState("");
    const [sfCampaigns, setSfCampaigns] = useState([]);
    const [campaignId, setCampaignId] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const fileRef = useRef(null);

    const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    const buildDataSource = () => {
        if (sourceKind === "sf_contacts" || sourceKind === "sf_leads") return { kind: sourceKind, filters };
        if (sourceKind === "sf_listview") {
            const lv = listviews.find((l) => l.id === listViewId);
            return { kind: "sf_listview", list_view_id: listViewId, list_view_object: lv?.object || lvObject };
        }
        if (sourceKind === "sf_report") return { kind: "sf_report", report_id: reportId, phone_column: phoneColumn || null };
        if (sourceKind === "sf_campaign") return { kind: "sf_campaign", sf_campaign_id: campaignId };
        return { kind: "csv" };
    };

    const manualRows = () =>
        manual.split(/[\n,]/).map((p) => p.trim()).filter(Boolean).map((phone) => ({ name: "", phone, email: "", company: "" }));

    // load SF option lists when the source changes
    useEffect(() => {
        setPreview(null);
        if (sourceKind === "sf_contacts" || sourceKind === "sf_leads") {
            api.get(`/whatsapp/broadcasts/sf-filter-options?kind=${sourceKind}`).then((r) => setFilterOpts(r.data)).catch(() => setFilterOpts({}));
        } else if (sourceKind === "sf_listview") {
            api.get("/whatsapp/broadcasts/sf-listview-objects").then((r) => setLvObjects(r.data.objects || [])).catch(() => setLvObjects([]));
        } else if (sourceKind === "sf_report") {
            api.get("/whatsapp/broadcasts/sf-reports").then((r) => setReports(r.data.reports || [])).catch(() => setReports([]));
        } else if (sourceKind === "sf_campaign") {
            api.get("/whatsapp/broadcasts/sf-campaigns").then((r) => setSfCampaigns(r.data.campaigns || [])).catch(() => setSfCampaigns([]));
        }
    }, [sourceKind]);

    useEffect(() => {
        if (sourceKind === "sf_listview" && lvObject)
            api.get(`/whatsapp/broadcasts/sf-listviews?sobject=${encodeURIComponent(lvObject)}`).then((r) => setListviews(r.data.listviews || [])).catch(() => setListviews([]));
    }, [lvObject, sourceKind]);

    // bubble the current selection up
    useEffect(() => {
        const ds = buildDataSource();
        const isCsv = sourceKind === "csv";
        const records = isCsv ? (csvRows.length ? csvRows : manualRows()) : [];
        const ready = isCsv ? records.length > 0 : !!(preview && preview.total > 0);
        onChange?.({ dataSource: ds, records, ready });
        // eslint-disable-next-line
    }, [sourceKind, csvRows, manual, filters, listViewId, lvObject, reportId, phoneColumn, campaignId, preview]);

    const onCsv = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => { setCsvRows(parseCsv(reader.result)); setCsvName(f.name); };
        reader.readAsText(f);
    };

    const runPreview = async () => {
        setPreviewing(true);
        try {
            const { data } = await api.post("/whatsapp/broadcasts/preview-source", { data_source: buildDataSource(), records: [] });
            setPreview(data);
        } catch {
            setPreview({ total: 0, sample: [] });
        } finally {
            setPreviewing(false);
        }
    };

    const PreviewBtn = () => (
        <button onClick={runPreview} disabled={previewing} data-testid={`${tidPrefix}-sf-preview`}
            className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {previewing ? <CircleNotch size={16} className="animate-spin" /> : <Users size={16} />} Preview audience
        </button>
    );

    return (
        <div>
            <div className="flex flex-wrap gap-2">
                {SOURCES.map(([k, l]) => (
                    <button key={k} data-testid={`${tidPrefix}-source-${k}`} onClick={() => { setSourceKind(k); setCsvRows([]); setCsvName(""); }}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${sourceKind === k ? "bg-emerald-500 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {sourceKind === "csv" && (
                <div className="mt-4">
                    <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onCsv} className="hidden" data-testid={`${tidPrefix}-csv-input`} />
                    <button onClick={() => fileRef.current?.click()} data-testid={`${tidPrefix}-csv-btn`}
                        className="inline-flex items-center gap-2 rounded-xl border border-dashed border-stone-300 px-4 py-3 text-sm font-semibold text-stone-600 hover:border-emerald-400">
                        <UploadSimple size={16} /> {csvName || "Upload CSV (phone, name, company, email)"}
                    </button>
                    {csvRows.length > 0 && <div className="mt-1 text-xs text-emerald-600">{csvRows.length} contacts loaded from CSV</div>}
                    <div className="mt-3 text-center text-xs text-stone2">or paste numbers</div>
                    <textarea data-testid={`${tidPrefix}-manual-input`} value={manual} onChange={(e) => setManual(e.target.value)} rows={3} disabled={csvRows.length > 0}
                        className="mt-2 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:border-ink disabled:bg-stone-50"
                        placeholder="+919876543210, +919812345678 …" />
                </div>
            )}

            {(sourceKind === "sf_contacts" || sourceKind === "sf_leads") && (
                <div className="mt-4 space-y-3" data-testid={`${tidPrefix}-filters`}>
                    <input value={filters.search || ""} onChange={(e) => setF("search", e.target.value)} placeholder="Search name / company / email"
                        className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" data-testid={`${tidPrefix}-filter-search`} />
                    <div className="grid gap-3 sm:grid-cols-3">
                        {sourceKind === "sf_leads" && <FilterSelect prefix={tidPrefix} label="Status" opts={filterOpts?.status} val={filters.status} onChange={(v) => setF("status", v)} tid="status" />}
                        <FilterSelect prefix={tidPrefix} label="Industry" opts={filterOpts?.industry} val={filters.industry} onChange={(v) => setF("industry", v)} tid="industry" />
                        <FilterSelect prefix={tidPrefix} label="State" opts={filterOpts?.state} val={filters.state} onChange={(v) => setF("state", v)} tid="state" />
                        <FilterSelect prefix={tidPrefix} label="Lead source" opts={filterOpts?.lead_source} val={filters.lead_source} onChange={(v) => setF("lead_source", v)} tid="lead_source" />
                        {sourceKind === "sf_leads" && <FilterSelect prefix={tidPrefix} label="Rating" opts={filterOpts?.rating} val={filters.rating} onChange={(v) => setF("rating", v)} tid="rating" />}
                        <FilterSelect prefix={tidPrefix} label="Owner" opts={(filterOpts?.owners || []).map((o) => ({ value: o.id, label: o.name }))} val={filters.owner_id} onChange={(v) => setF("owner_id", v)} tid="owner" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="text-xs text-stone2">Created after
                            <input type="date" value={filters.created_after || ""} onChange={(e) => setF("created_after", e.target.value)}
                                className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" data-testid={`${tidPrefix}-filter-created-after`} /></label>
                        <label className="text-xs text-stone2">Created before
                            <input type="date" value={filters.created_before || ""} onChange={(e) => setF("created_before", e.target.value)}
                                className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" /></label>
                    </div>
                    <PreviewBtn />
                </div>
            )}

            {sourceKind === "sf_listview" && (
                <div className="mt-4 space-y-3">
                    <select data-testid={`${tidPrefix}-lv-object`} value={lvObject} onChange={(e) => setLvObject(e.target.value)}
                        className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                        <option value="">Choose object…</option>
                        {lvObjects.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {lvObject && (
                        <select data-testid={`${tidPrefix}-lv-view`} value={listViewId} onChange={(e) => { setListViewId(e.target.value); setPreview(null); }}
                            className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                            <option value="">Choose a list view…</option>
                            {listviews.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                    )}
                    {listViewId && <PreviewBtn />}
                </div>
            )}

            {sourceKind === "sf_report" && (
                <div className="mt-4 space-y-3">
                    <select data-testid={`${tidPrefix}-report`} value={reportId} onChange={(e) => { setReportId(e.target.value); setPreview(null); }}
                        className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                        <option value="">Choose a tabular report…</option>
                        {reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <input value={phoneColumn} onChange={(e) => setPhoneColumn(e.target.value)} placeholder="Phone column name in the report (optional — auto-detected)"
                        className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" data-testid={`${tidPrefix}-report-phone-col`} />
                    {reportId && <PreviewBtn />}
                </div>
            )}

            {sourceKind === "sf_campaign" && (
                <div className="mt-4 space-y-3">
                    <select data-testid={`${tidPrefix}-campaign`} value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setPreview(null); }}
                        className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                        <option value="">Choose a campaign…</option>
                        {sfCampaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {campaignId && <PreviewBtn />}
                </div>
            )}

            {preview && (
                <div data-testid={`${tidPrefix}-preview`} className="mt-4 rounded-xl border border-stone-100 bg-stone-50 p-4">
                    <div className="text-sm font-bold">{preview.total} recipient{preview.total === 1 ? "" : "s"} {preview.capped ? "(capped at 500)" : ""}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {(preview.sample || []).map((r, i) => (
                            <span key={i} className="rounded-full bg-white px-3 py-1 text-xs text-stone-600 shadow-sm">{r.name || r.phone}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AudiencePicker;
